/** Shared signed-out empty state + hooks into login / Settings / post-login refresh. */

import { fetchAuthMe } from "@shared/ui/sessionAuth";

type AuthUiHooks = {
  openSettings: () => void;
  openLogin?: () => void;
  onAuthChanged?: () => void;
};

let hooks: AuthUiHooks | null = null;
let authCache: { at: number; authenticated: boolean } | null = null;
const AUTH_CACHE_MS = 8_000;

export function registerAuthUiHooks(next: AuthUiHooks): void {
  hooks = next;
}

export function openAuthSettings(): void {
  hooks?.openSettings();
}

export function openAuthLogin(): void {
  if (hooks?.openLogin) {
    hooks.openLogin();
    return;
  }
  hooks?.openSettings();
}

export function invalidateSessionCache(): void {
  authCache = null;
}

export function notifyAuthChanged(): void {
  authCache = null;
  hooks?.onAuthChanged?.();
}

export async function isSessionAuthenticated(): Promise<boolean> {
  if (authCache && Date.now() - authCache.at < AUTH_CACHE_MS) {
    return authCache.authenticated;
  }
  try {
    const me = await fetchAuthMe();
    authCache = { at: Date.now(), authenticated: Boolean(me.authenticated) };
    return authCache.authenticated;
  } catch {
    authCache = { at: Date.now(), authenticated: false };
    return false;
  }
}

export function isUnauthorizedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const status = (err as { status?: number }).status;
  if (status === 401) return true;
  const code = (err as { code?: string }).code;
  if (code === "unauthorized") return true;
  const message = err instanceof Error ? err.message : String(err);
  return /sign in required|unauthorized/i.test(message);
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const DEFAULT_BLURB =
  "Sign in with your Stock Forge+ account to view positions, recommendations, tax planning, and analytics.";

export function renderSignInRequiredHtml(opts: {
  title: string;
  view?: string;
  blurb?: string;
}): string {
  const viewAttr = opts.view ? ` data-wheel-view="${esc(opts.view)}"` : "";
  const blurb = opts.blurb ?? DEFAULT_BLURB;
  return `
    <div class="panel"${viewAttr}>
      <h2>${opts.title}</h2>
      <div class="sfe-signin-required" role="status">
        <p class="sfe-signin-required-title">Sign in required</p>
        <p class="muted small sfe-signin-required-blurb">${esc(blurb)}</p>
        <button type="button" class="small" data-action="sfe-open-signin">Sign in</button>
      </div>
    </div>`;
}

export function renderSignInRequiredHeaderHtml(opts: {
  title: string;
  blurb?: string;
}): string {
  const blurb = opts.blurb ?? DEFAULT_BLURB;
  return `
    <div class="sfe-custom-panel-header">
      <h2>${opts.title}</h2>
      <div class="sfe-signin-required" role="status">
        <p class="sfe-signin-required-title">Sign in required</p>
        <p class="muted small sfe-signin-required-blurb">${esc(blurb)}</p>
        <button type="button" class="small" data-action="sfe-open-signin">Sign in</button>
      </div>
    </div>`;
}

export function paintSignInRequired(
  panel: HTMLElement,
  opts: { title: string; view?: string; blurb?: string; headerOnly?: boolean },
): void {
  panel.innerHTML = opts.headerOnly
    ? renderSignInRequiredHeaderHtml({ title: opts.title, blurb: opts.blurb })
    : renderSignInRequiredHtml({ title: opts.title, view: opts.view, blurb: opts.blurb });
  bindSignInRequired(panel);
}

/** Returns false and paints the sign-in empty state when not authenticated. */
export async function ensureSignedIn(
  panel: HTMLElement,
  opts: { title: string; view?: string; blurb?: string },
): Promise<boolean> {
  if (await isSessionAuthenticated()) return true;
  paintSignInRequired(panel, opts);
  return false;
}

/** Paint a panel load failure — friendly empty state for 401, otherwise red error. */
export function paintPanelLoadError(
  panel: HTMLElement,
  err: unknown,
  opts: { title: string; view?: string; fallback: string; headerOnly?: boolean },
): void {
  if (isUnauthorizedError(err)) {
    paintSignInRequired(panel, {
      title: opts.title,
      view: opts.view,
      headerOnly: opts.headerOnly,
    });
    return;
  }
  const message = err instanceof Error ? err.message : opts.fallback;
  if (opts.headerOnly) {
    panel.innerHTML = `
      <div class="sfe-custom-panel-header">
        <h2>${opts.title}</h2>
        <p class="sfe-web-error">${esc(message)}</p>
      </div>`;
    return;
  }
  const viewAttr = opts.view ? ` data-wheel-view="${esc(opts.view)}"` : "";
  panel.innerHTML = `
    <div class="panel"${viewAttr}>
      <h2>${opts.title}</h2>
      <p class="sfe-web-error">${esc(message)}</p>
    </div>`;
}

export function bindSignInRequired(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('[data-action="sfe-open-signin"]').forEach((btn) => {
    btn.addEventListener("click", () => openAuthLogin());
  });
}
